import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Pencil } from 'lucide-react';
import { Card, CardBody, CardHeader } from './ui/Card';
import { useConfirm } from './ui/ConfirmDialog';
import { CountrySelect } from './ui/CountrySelect';
import { CurrencySelect } from './ui/CurrencySelect';
import { api } from '../lib/api';

interface Entity {
  id: string;
  name: string;
  country_code: string;
  base_currency: string;
  fiscal_year_start_month: number | null;
}

const inputCn =
  'px-2 py-1.5 text-sm rounded-md border border-ink-300 dark:border-ink-700 bg-white dark:bg-ink-900 focus:outline-none focus:ring-2 focus:ring-accent-500';

/** Multi-entity management — create / delete legal entities under the workspace. */
export function EntitiesCard() {
  const confirm = useConfirm();
  const { wsId } = useParams<{ wsId: string }>();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [country, setCountry] = useState('JP');
  const [currency, setCurrency] = useState('JPY');
  const [fyStart, setFyStart] = useState(4);
  const [error, setError] = useState<string | null>(null);

  const entities = useQuery<Entity[]>({
    queryKey: ['entities', wsId],
    enabled: !!wsId,
    queryFn: async () => (await api.get<Entity[]>(`/workspaces/${wsId}/entities`)).data,
  });
  const save = useMutation({
    mutationFn: async () => {
      const body = { name, countryCode: country.toUpperCase().slice(0, 2), baseCurrency: currency.toUpperCase().slice(0, 3), fiscalYearStartMonth: fyStart };
      return editingId
        ? (await api.patch(`/workspaces/${wsId}/entities/${editingId}`, body)).data
        : (await api.post(`/workspaces/${wsId}/entities`, body)).data;
    },
    onSuccess: () => { setOpen(false); setEditingId(null); setName(''); setError(null); qc.invalidateQueries({ queryKey: ['entities', wsId] }); },
    onError: (err: any) => setError(err?.response?.data?.message ?? err?.message ?? 'Failed'),
  });
  const del = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/workspaces/${wsId}/entities/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['entities', wsId] }),
    onError: (err: any) => setError(err?.response?.data?.message ?? err?.message ?? 'Failed'),
  });

  return (
    <Card className="mb-5">
      <CardHeader>
        <div className="flex items-center justify-between">
          <span className="font-medium">{t('settings.entities.title')}</span>
          <button type="button" onClick={() => { setEditingId(null); setName(''); setCountry('JP'); setCurrency('JPY'); setFyStart(4); setOpen((o) => !o); }} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-accent-600 text-white text-xs font-medium hover:bg-accent-700">
            <Plus size={12} /> {t('settings.entities.add')}
          </button>
        </div>
      </CardHeader>
      <CardBody className="space-y-3">
        {open && (
          <div className="grid sm:grid-cols-4 gap-2 p-2 rounded-lg border border-ink-200 dark:border-ink-800 bg-ink-50 dark:bg-ink-900/40">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('settings.entities.name')} className={`${inputCn} sm:col-span-2`} />
            <CountrySelect value={country} onChange={setCountry} />
            <CurrencySelect value={currency} onChange={setCurrency} />
            <select value={fyStart} onChange={(e) => setFyStart(Number(e.target.value))} className={inputCn} title={t('settings.entities.fiscalStart')}>
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => <option key={m} value={m}>{t('settings.entities.fyMonth', { m })}</option>)}
            </select>
            <button type="button" onClick={() => name.trim() ? save.mutate() : setError(t('settings.entities.nameRequired'))} disabled={save.isPending} className="px-3 py-1.5 rounded-lg bg-accent-600 text-white text-xs font-medium hover:bg-accent-700 disabled:opacity-50">
              {t('common.save')}
            </button>
          </div>
        )}
        {error && <div className="text-xs text-warn-600">{error}</div>}
        {entities.isLoading ? (
          <div className="text-xs text-ink-500">{t('common.loading')}</div>
        ) : !entities.data?.length ? (
          <div className="text-xs text-ink-500">{t('settings.entities.none')}</div>
        ) : (
          <ul className="divide-y divide-ink-200 dark:divide-ink-800">
            {entities.data.map((e) => (
              <li key={e.id} className="py-2 flex items-center justify-between text-sm">
                <div>
                  <div className="font-medium">{e.name}</div>
                  <div className="text-xs text-ink-500 num">{e.country_code} · {e.base_currency}</div>
                </div>
                <div className="flex items-center gap-1">
                  <button type="button" onClick={() => { setEditingId(e.id); setName(e.name); setCountry(e.country_code); setCurrency(e.base_currency); setFyStart(e.fiscal_year_start_month ?? 4); setOpen(true); }} className="p-1.5 rounded-md text-ink-400 hover:text-accent-600" title={t('common.edit')}>
                    <Pencil size={14} />
                  </button>
                  <button type="button" onClick={async () => { if (await confirm({ title: t('common.confirmDelete'), confirmLabel: t('common.delete'), destructive: true })) del.mutate(e.id); }} className="p-1.5 rounded-md text-ink-400 hover:text-warn-600" title={t('common.delete')}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}
